import cx from "classnames";
import type { HTMLAttributes, MouseEvent, Ref } from "react";
import { forwardRef, useCallback } from "react";

import CS from "metabase/css/core/index.css";
import { Box, Center, UnstyledButton } from "metabase/ui";
import type { ColorName } from "metabase/ui/colors/types";

import ColorPillS from "./ColorPill.module.css";
import type { PillSize } from "./types";

export type ColorPillAttributes = Omit<
  HTMLAttributes<HTMLElement>,
  "onSelect" | "onClick"
>;

export interface ColorPillProps extends ColorPillAttributes {
  color: ColorName | string;
  isAuto?: boolean;
  isSelected?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onSelect?: (newColor: string) => void;
  pillSize?: PillSize;
  "data-testid"?: string;
  className?: string;
}

export const ColorPill = forwardRef(function ColorPill(
  {
    color,
    isAuto = false,
    isSelected = true,
    "aria-label": ariaLabel = color,
    pillSize = "medium",
    onClick,
    onSelect,
    "data-testid": dataTestId,
    className,
    ...props
  }: ColorPillProps,
  ref: Ref<HTMLButtonElement>,
) {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      onClick?.(event);
      onSelect?.(color);
    },
    [color, onClick, onSelect],
  );

  const pillClassName = cx(
    ColorPillS.ColorPill,
    CS.flexNoShrink,
    {
      [ColorPillS.XSmall]: pillSize === "xsmall",
      [ColorPillS.Small]: pillSize === "small",
      [ColorPillS.Medium]: pillSize === "medium",
      [ColorPillS.Large]: pillSize === "large",
      [ColorPillS.Selected]: isSelected,
      [ColorPillS.Auto]: isAuto,
    },
    className,
  );

  const swatch = (
    // @ts-expect-error color pill needs access to arbitrary color values
    <Box bg={color} w="100%" h="100%" style={{ borderRadius: "50%" }}></Box>
  );

  // A ColorPill is only ever interactive when a caller wired up a click or
  // selection handler -- e.g. as the trigger of a color picker popover. The
  // decorative usage in scalar_utils.tsx renders one with neither, purely as
  // a swatch inside a tooltip legend; giving that a tab stop and a
  // misleading `role="button"` would be its own accessibility bug, so it
  // stays a plain, non-interactive element instead.
  if (onClick == null && onSelect == null) {
    return (
      <Center
        {...props}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        className={pillClassName}
      >
        {swatch}
      </Center>
    );
  }

  return (
    <UnstyledButton
      {...props}
      ref={ref}
      type="button"
      data-testid={dataTestId}
      aria-label={ariaLabel}
      onClick={handleClick}
      className={pillClassName}
    >
      {swatch}
    </UnstyledButton>
  );
});
